import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Scissors, User, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';

export const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const { user, isAuthenticated, logout } = useAuth();
    const showWhatsApp = !location.pathname.startsWith('/admin')
        && !location.pathname.startsWith('/team')
        && !location.pathname.startsWith('/login')
        && !location.pathname.startsWith('/book');

    const navLinks = [
        { href: '/', label: 'Home' },
        { href: '/services', label: 'Services' },
        { href: '/book', label: 'Book Now' },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <>
            <nav className="fixed top-0 left-0 right-0 z-50 glass">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/03303zlc_96f21524-c6ba-4411-86fb-3aa42208d697.jpeg" 
                            alt="RL Barbershop" 
                            className="h-12 w-12 object-contain"
                        />
                        <span className="font-anton text-xl hidden sm:block">RL BARBERSHOP</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                                className={`font-urbanist font-medium tracking-wide transition-colors ${
                                    isActive(link.href) 
                                        ? 'text-primary' 
                                        : 'text-zinc-400 hover:text-white'
                                }`}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    {/* Auth / Dashboard Links */}
                    <div className="hidden md:flex items-center gap-4">
                        {isAuthenticated ? (
                            <>
                                <Link
                                    to={user?.role === 'admin' ? '/admin' : '/team'}
                                    data-testid="dashboard-link"
                                    className="flex items-center gap-2 text-zinc-400 hover:text-white"
                                >
                                    <User size={18} />
                                    <span className="font-urbanist">{user?.name}</span>
                                </Link>
                                <Button
                                    variant="outline"
                                    onClick={logout}
                                    data-testid="logout-btn"
                                    className="border-zinc-700 text-zinc-400 hover:text-white hover:border-white"
                                >
                                    Logout
                                </Button>
                            </>
                        ) : (
                            <Link to="/login" data-testid="login-link">
                                <Button variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white hover:border-white">
                                    Staff Login
                                </Button>
                            </Link>
                        )}
                        <Link to="/book" data-testid="nav-book-now-btn">
                            <Button className="bg-primary text-black font-anton tracking-wider hover:bg-white neon-glow">
                                <Scissors size={18} className="mr-2" />
                                BOOK NOW
                            </Button>
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="md:hidden text-white p-2"
                        data-testid="mobile-menu-btn"
                    >
                        {isOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {isOpen && (
                    <div className="md:hidden mobile-menu pt-20" data-testid="mobile-menu">
                        <div className="px-6 py-8 space-y-6">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    onClick={() => setIsOpen(false)}
                                    className={`block font-anton text-2xl ${
                                        isActive(link.href) ? 'text-primary' : 'text-white'
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                            <div className="pt-6 border-t border-zinc-800">
                                {isAuthenticated ? (
                                    <>
                                        <Link
                                            to={user?.role === 'admin' ? '/admin' : '/team'}
                                            onClick={() => setIsOpen(false)}
                                            className="block font-urbanist text-lg text-zinc-400 mb-4"
                                        >
                                            Dashboard
                                        </Link>
                                        <button
                                            onClick={() => { logout(); setIsOpen(false); }}
                                            className="block font-urbanist text-lg text-zinc-400"
                                        >
                                            Logout
                                        </button>
                                    </>
                                ) : (
                                    <Link
                                        to="/login"
                                        onClick={() => setIsOpen(false)}
                                        className="block font-urbanist text-lg text-zinc-400"
                                    >
                                        Staff Login
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            </nav>

            {showWhatsApp && (
                <a
                    href="https://wa.me/35699336503"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-floating-button"
                    className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-[#25D366] text-black font-urbanist font-semibold shadow-lg hover:brightness-110"
                >
                    <MessageCircle size={18} />
                    WhatsApp
                </a>
            )}
        </>
    );
};

export default Navbar;
